/**
 * useHomepageCards
 *
 * The journey a Home's guided plan follows, and where the viewer is in it.
 * It used to order a stack of cards as well; that stack is gone, and the
 * guided pre-cycle homes read the journey and its progress directly.
 */

import { useMemo } from "react";
import type { HomepageGender, HomepageGoal } from "../../homepage.types";
import type {
  JourneyDefinition,
  Pathway,
  PlanStep,
  TreatmentModality,
  JourneyViewerRole,
} from "../plan.types";
import { resolveHomepageJourney } from "../resolveHomepageJourney";
import { usePlanProgress, type StepProgress } from "./usePlanProgress";

export type UseHomepageCardsResult = {
  journey: JourneyDefinition;
  progress: StepProgress[];
  activeStepIndex: number;
  activeStep: PlanStep | null;
};

/**
 * Find the first in-progress step
 */
function findActiveStepIndex(progress: StepProgress[]): number {
  const idx = progress.findIndex((p) => p.status === "in-progress");
  return idx >= 0 ? idx : 0;
}

/**
 * Main hook: resolves the journey and the viewer's progress through it
 */
export function useHomepageCards(
  gender: HomepageGender | null,
  goal: HomepageGoal | null,
  pathway: Pathway,
  modality?: TreatmentModality | null,
  viewerRole?: JourneyViewerRole | null,
): UseHomepageCardsResult {
  const journey = useMemo(
    () => resolveHomepageJourney(gender, goal, pathway, modality, viewerRole),
    [gender, goal, pathway, modality, viewerRole],
  );

  const { progress } = usePlanProgress(journey.steps, journey.key);

  const activeStepIndex = useMemo(
    () => findActiveStepIndex(progress),
    [progress],
  );

  const activeStep = journey.steps[activeStepIndex] ?? null;

  return {
    journey,
    progress,
    activeStepIndex,
    activeStep,
  };
}
